import { DataManager } from "../model/DataManager.js";
import { VOCategory, VOHome, VOHomeCategory, VOHomeElement, VOImage } from "../model/DataStructure.js";
import { Editor } from "./Editor.js";
import { AbstractImageItem } from "./items/AbstractImageItem.js";
import { AnimatedPixiImageItem } from "./items/AnimatedPixiImageItem.js";
import { EditingImageItem } from "./items/EditingImageItem.js";
import { HtmlImageItem } from "./items/HtmlImageItem.js";
import { PixiImageItem } from "./items/PixiImageItem.js";
import { Menu } from "./Menu.js";
import { Minimap } from "./Minimap.js";

export class Home {
    public onOpenCategory = e5.Signal.create<[VOCategory]>();
    public onChangeActive = e5.Signal.create<[boolean]>();
    public onChangeX = e5.Signal.create<[number]>();

    public element: HTMLDivElement;
    public home: VOHome;

    private _items: AbstractImageItem<any>[] = [];
    private _x: number = -1;
    private _mapContainer: HTMLDivElement;
    private _labelContainer: HtmlImageItem;
    private _left: HTMLDivElement;
    private _right: HTMLDivElement;
    private _targetX: number = NaN;
    private _homeCategories: VOHomeCategory[] = [];
    private _app: PIXI.Application;
    private _map: HTMLImageElement;
    private _editor: Editor;
    private _editing: boolean = true;
    private _usePixi: boolean = false;

    constructor(private _data: DataManager, private _screenWidth: number, private _screenHeight: number) {
        this.element = document.createElement("div");
        this.element.id = "home";
        this.element.classList.add("active");
        this.element.innerHTML = `
        <div id="map"></div>
        <div class="direction left icon-125"></div>
        <div class="direction right icon-124"></div>
        `;
        this._mapContainer = this.element.querySelector("#map");
        this._left = this.element.querySelector(".direction.left");
        this._right = this.element.querySelector(".direction.right");

        this._editing = e5.Url.params.has("edit");

        //Make the PIXI filters available for TypeScript :(
        (window as any).pixiFilters = PIXI.filters as any;

        //TODO: get home by configuration...
        this.home = this._data.data.home[0];
        this._homeCategories = this.home.categories;
    }

    public async create(): Promise<boolean> {
        //Becoming a jerk face :)
        PIXI.utils.skipHello();

        this._app = new PIXI.Application({
            width: this._screenWidth,
            height: this._screenHeight,
            resolution: 1
        });
        this._app.view.style.position = "absolute";
        this._app.view.style.top = "0";
        this._app.view.style.left = "0";
        this._mapContainer.appendChild(this._app.view);

        //Background - clouds, water, etc.
        var bg = await this.loadImage(this.home.background.url);
        this.addImageCropped(bg, 1.2);

        //Foreground - Floe, people, etc.
        this._map = await this.loadImage(this.home.map.url);
        this.addImageCropped(this._map, 1);

        // this._app.stage.filters = [new pixiFilters.GodrayFilter()];

        //Clamp homeCategory.targetX
        for (var homeCategory of this.home.categories) {
            homeCategory.targetX = e5.Calc.clamp(Math.round(-homeCategory.home_category_x + this._screenWidth * 0.5), this.minX, this.maxX);
        }

        //Frontend editing :)
        if (this._editing) {
            this._editor = new Editor(this);
            document.getElementById("app").appendChild(this._editor.element);
        }

        //Add each element
        for (var element of this.home.elements) {
            this.addElement(element);
        }

        //The overlay for the labels.
        this._labelContainer = this.createDummyItem(0, 0, this._map.width, this._map.height);
        this._items.push(this._labelContainer);
        this._mapContainer.appendChild(this._labelContainer.element);

        //Add markers to map
        for (var homeCategory of this.home.categories) {
            var e = document.createElement("div");
            e.className = "nav-marker";
            e.style.transform = `translate(-50%, -50%) translate(${homeCategory.home_category_x}px, ${homeCategory.home_category_y}px)`;
            e.innerHTML = `<span class="nav-marker-trigger icon-49"></span><span class="nav-marker-label">${homeCategory.home_category.title}</span>`;
            this._labelContainer.element.appendChild(e);
        }

        //Add lines for the category boundaries
        if (this._editing) {

            for (var i = 1; i < this.home.categories.length; ++i) {
                var a = this.home.categories[i - 1];
                var b = this.home.categories[i];
                var ax = -a.targetX + this._screenWidth * 0.5;
                var bx = -b.targetX + this._screenWidth * 0.5;
                var xPos = ax + (bx - ax) * 0.5;
                var line = this.createDummyItem(xPos - 1, 0, 2, this._screenHeight, 1);
                line.element.classList.add("category-boundary");
                this._items.push(line);
                this._mapContainer.appendChild(line.element);
            }
        }

        //Main menu (About, Press, ...)
        var menu = new Menu(this.home);
        menu.onRequestCategory.add((c) => this.open(c));
        this.onChangeActive.add((active) => {
            menu.active = active;
        });
        this.element.appendChild(menu.element);

        //Minimap for the VOHomeCategory items
        var minimap = new Minimap(this._data, this, this._map, this._screenWidth);
        minimap.onRequestHomeCategory.add((c) => this.tweenToHomeCategory(c));
        this.onChangeActive.add((active) => minimap.active = active);
        this.element.appendChild(minimap.element);

        this.x = 0;

        // document.addEventListener("keyup", () => {
        //     this.ripple(actor.pointer.x, actor.pointer.y);
        // });

        var actor = new e5.Click(this._mapContainer, {
            preventDefault: true
        });

        actor.onDown.add(() => {
            this._targetX = NaN;
            gsap.killTweensOf(this);

        });

        actor.onMove.add(() => {
            var local = e5.Transform.toLocalDelta(this.element, actor.pointer.deltaX, actor.pointer.deltaY);
            this.x += local.x;
        });

        actor.onUp.add((pointers) => {
            if (actor.pointers.length === 0) {
                if (pointers[0].isClick)
                    return;

                var x = this.x + actor.pointer.velocityX * 500;
                var homeCategory = this.getHomeCategoryByPosition(x);
                if (homeCategory) {
                    if (actor.pointer.velocityX > 0) {
                        var left = this.getLeftX(x);
                        if (left > x)
                            x = left;
                    } else {
                        var right = this.getRightX(x);
                        if (right < x)
                            x = right;
                    }
                }
                this.tweenTo(x);
            }
        });

        actor.onClick.add(() => {
            var idx = this.getCurrentHomeCategoryIndex();
            var homeCategory = this._homeCategories[idx];
            if (homeCategory)
                this.open(homeCategory.home_category);
        });

        this._left.addEventListener("click", () => this.handleClickLeft());

        this._right.addEventListener("click", () => this.handleClickRight());

        // $(this.element).on("click", "[data-category]", (e) => {
        //     var categoryId = parseInt(e.currentTarget.getAttribute("data-category"));
        //     var category = this._data.data.category.id(categoryId);
        //     this.openOrTweenTo(category);
        // });

        $(this.element).on("click", "[data-homecategory]", (e) => {
            var homeCategoryId = parseInt(e.currentTarget.getAttribute("data-homecategory"));
            var homeCategory = this._homeCategories.id(homeCategoryId);
            this.openOrTweenToHomeCategory(homeCategory);
        });

        if (this._data.config.initial_category) {
            var hc = this.getHomeCategory(this._data.config.initial_category);
            if (hc) {
                this.tweenTo(hc.targetX, 0);
            }
        }

        return true;
    }

    /**
     * Divide the image in 1920 x 1080 canvas elements (better performance).
     * 
     * @param image The image to crop/divide into smaller pieces
     * @param z The z-position of the elements.
     */
    private addImageCropped(image: HTMLImageElement, z: number): void {
        //Divide the large map image into smaller pieces
        var divideWidth = this._screenWidth;
        var count = Math.ceil(image.width / divideWidth);
        for (var i = 0; i < count; ++i) {
            var can = document.createElement("canvas");
            can.width = e5.Calc.clamp(divideWidth, 1, image.width - (i * divideWidth));
            can.height = image.height;
            var ctx = can.getContext("2d");
            ctx.drawImage(image, -(i * divideWidth), 0);

            var img: VOImage = {
                x: i * divideWidth,
                y: 0,
                z: z,
                canvas: can,
                image: {
                    author: "",
                    description: "",
                    width: can.width,
                    height: can.height,
                    sizes: null,
                    subtitle: "",
                    title: "",
                    type: "image",
                    url: null,
                    languages: "all"
                }
            };

            this.addImage(img, this._usePixi ? "pixi" : "html");
        }
    }

    /**
     * Returns the first VOHomeCategory related to the given VOCategory.
     */
    public getHomeCategory(category: VOCategory): VOHomeCategory {
        return e5.array.match(this.home.categories, "home_category", category);
    }

    /**
     * Removes an EditingImageItem from:
     * - the current VOHome category array
     * - the Editor
     * 
     * @param item The element to remove.
     */
    public removeElement(item: EditingImageItem): void {
        this._items.splice(this._items.indexOf(item), 1);
        this.home.elements.splice(this.home.elements.indexOf(item.image), 1);
        if (this._editing) {
            this._editor.removeItem(item);
        }
    }

    /**
     * Adds an VOHomeElement to the scene/map.
     */
    public addElement(element: VOHomeElement): AbstractImageItem<any> {
        element.z = Math.max(element.z, 0.01);

        if (!element.category) {
            element.category = this.getCategoryForElement(element);
        }

        if (element.category) {
            //When the category is locked/in-view the element should be positioned
            //exactly at the give x-position. Tricky but nice :)
            var tx = element.category.targetX;
            var x = element.x;
            var z = element.z;
            element.x = (tx + x) - (tx / z);
        }

        var item = this.addImage(element, this._editing ? "editing" : (this._usePixi ? "animated" : "html"));
        if (this._editing) {
            this._editor.addItem(item as EditingImageItem);
        }

        return item;
    }

    /**
     * Returns a VOHomeCategory for an VOHomeElement
     * based on the elements position and size.
     * 
     * @param element An VOHomeElement
     */
    public getCategoryForElement(element: VOHomeElement): VOHomeCategory {
        return this.getHomeCategoryByPosition(-(element.x + element.image.width * 0.5) + this._screenWidth * 0.5)
    }

    private createDummyItem(x: number, y: number, width: number, height: number, z: number = 1): HtmlImageItem {
        return new HtmlImageItem({
            x: x,
            y: y,
            z: z,
            canvas: null,
            image: {
                author: "",
                description: "",
                width: width,
                height: height,
                sizes: null,
                subtitle: "",
                title: "",
                type: "image",
                url: null,
                languages: "all"
            }
        }, this._screenWidth);
    }

    /**
     * Createas and image item and adds it to the scene.
     * 
     * @param image The VOImage to add
     * @param type Type of image item to create.
     */
    private addImage(image: VOImage, type: "html" | "pixi" | "animated" | "editing"): AbstractImageItem<any> {
        if (type === "animated") {
            var itemA = new AnimatedPixiImageItem(image, this._screenWidth);
            this._items.push(itemA);
            this._app.stage.addChild(itemA.element);
            return itemA;
        } else if (type === "html") {
            var itemH = new HtmlImageItem(image, this._screenWidth);
            this._items.push(itemH);
            this._mapContainer.appendChild(itemH.element);
            return itemH;
        } else if (type === "editing") {
            var itemE = new EditingImageItem(this, this._editor, image, this._screenWidth);
            this._items.push(itemE);
            this._mapContainer.appendChild(itemE.element);
            return itemE;
        } else if (type === "pixi") {
            var itemP = new PixiImageItem(image, this._screenWidth);
            this._items.push(itemP);
            this._app.stage.addChild(itemP.element);
            return itemP;
        }

        return null;
    }

    private ripple(clientX: number, clientY: number): void {
        var local = e5.Transform.toLocal(this.element, clientX, clientY);
        var filter = new pixiFilters.ShockwaveFilter(new PIXI.Point(local.x, local.y), null, 0.2);
        const maxTime = 2.5;
        var k = () => {
            filter.time += this._app.ticker.elapsedMS / 500;
            if (filter.time > maxTime) {
                this._app.stage.filters.splice(this._app.stage.filters.indexOf(filter), 1);
                this._app.ticker.remove(k);
            }
            filter.time %= maxTime;
        };
        this._app.ticker.add(k);
        if (this._app.stage.filters)
            this._app.stage.filters.push(filter);
        else
            this._app.stage.filters = [filter];
    }

    private async loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise<HTMLImageElement>((resolve, reject) => {
            var img = document.createElement("img");
            img.onload = () => {
                resolve(img);
            };
            img.src = url;
        });
    }

    public open(category: VOCategory, tweenTo: boolean = false): void {
        if (!category) {
            console.warn("Could not open category. Category is null.");
            return;
        }

        this.active = false;

        if (tweenTo) {
            var homeCategory = e5.array.match(this._homeCategories, "home_category", category);
            if (homeCategory) {
                this.tweenTo(homeCategory.targetX);
            }
        }

        this.onOpenCategory.dispatch(category);
    }

    public openOrTweenToHomeCategory(homeCategory: VOHomeCategory): void {
        if (!homeCategory)
            return;
        var current = this.getCurrentHomeCategory();
        if (current === homeCategory) {
            this.open(homeCategory.home_category);
        }
        else {
            this.tweenToHomeCategory(homeCategory);
        }
    }

    public get active(): boolean {
        return this.element.classList.contains("active");
    }

    public set active(active: boolean) {
        if (this.active === active)
            return;
        this.element.classList.toggle("active", active);
        this.onChangeActive.dispatch(active);

        if (active)
            this._app.start();
        else
            this._app.stop();
    }

    private _idleSpeed: number = 1;
    public idleNext(time: number = 3): void {
        // var idx = e5.Calc.wrap(this.getCurrentHomeCategoryIndex() + 1, this._homeCategories.length);
        // this.tweenTo(this._homeCategories[idx].targetX);

        var idx = this.getCurrentHomeCategoryIndex();
        if (idx + this._idleSpeed >= this._homeCategories.length) {
            this._idleSpeed = -1;
        } else if (idx + this._idleSpeed < 0) {
            this._idleSpeed = 1;
        }

        this.tweenTo(this._homeCategories[idx + this._idleSpeed].targetX, time);
    }

    private handleClickLeft(): void {
        this.tweenTo(this.getLeftX());
    }

    private handleClickRight(): void {
        this.tweenTo(this.getRightX());
    }

    private getLeftX(targetX: number = this.targetX): number {
        for (var i = this._homeCategories.length - 1; i >= 0; --i) {
            if (this._homeCategories[i].targetX > targetX)
                return this._homeCategories[i].targetX;
        }
        return 0;

        // if (this._categories.length === 0)
        //     return 0;
        // var idx = this.getCurrentCategoryIndex();
        // return this._categories[e5.Calc.clamp(idx - 1, 0, this._categories.length - 1)].targetX;
    }

    private getRightX(targetX: number = this.targetX): number {
        for (var i = 0; i < this._homeCategories.length; ++i) {
            if (this._homeCategories[i].targetX < targetX) {
                return this._homeCategories[i].targetX;
            }
        }
        return 0;


        // if (this._categories.length === 0)
        //     return 0;
        // var idx = this.getCurrentCategoryIndex();
        // return this._categories[e5.Calc.clamp(idx + 1, 0, this._categories.length - 1)].targetX;
    }

    public getCurrentHomeCategory(): VOHomeCategory {
        return this._homeCategories[this.getCurrentHomeCategoryIndex(this.targetX)];
    }

    private getCurrentHomeCategoryIndex(x: number = this.targetX): number {
        var cat = this.getHomeCategoryByPosition(x);
        if (cat)
            return this._homeCategories.indexOf(cat);
        return -1;
    }

    public getHomeCategoryByPosition(x: number = this.targetX): VOHomeCategory {
        var result: VOHomeCategory = null;
        var distX = Number.MAX_VALUE;
        for (var homeCategory of this._homeCategories) {
            var dist = Math.abs(x - homeCategory.targetX);
            if (dist < distX) {
                distX = dist;
                result = homeCategory;
            }
        }
        return result;
    }

    private updateArrowState(): void {
        var idx = this.getCurrentHomeCategoryIndex();
        this._left.classList.toggle("active", idx > 0);
        this._right.classList.toggle("active", idx < this._homeCategories.length - 1);
    }

    public tweenToHomeCategory(category: VOHomeCategory): void {
        if (category)
            this.tweenTo(category.targetX);
    }

    public tweenToCategory(category: VOCategory): void {
        this.tweenToHomeCategory(e5.array.match(this.home.categories, "home_category", category));
    }

    private tweenTo(x: number, time: number = NaN): void {
        x = Math.round(e5.Calc.clamp(x, this.minX, this.maxX));
        if (this.x === x) {
            return;
        }

        if (isNaN(time)) {
            time = Math.abs(this._x - x) / 3000;
            time = 1;
        }

        this._targetX = x;
        gsap.to(this, {
            duration: time,
            x: x,
            // ease: gs.Cubic.easeInOut,
            onComplete: () => {
                this._targetX = NaN;
            }
        });
    }

    private get minX(): number {
        return -this._map.width + this._screenWidth;
    }

    private get maxX(): number {
        return 0;
    }

    private get targetX(): number {
        return isNaN(this._targetX) ? this.x : this._targetX;
    }

    public get x(): number {
        return this._x;
    }

    public set x(x: number) {
        x = e5.Calc.clamp(x, this.minX, this.maxX);
        if (this._x === x)
            return;
        this._x = x;

        this.update();
    }

    public update(): void {
        for (var item of this._items) {
            item.x = item.left + (this._x / item.z);
        }
        this.updateArrowState();
        this.onChangeX.dispatch(this._x);
    }
}